import { useEffect, useState } from 'react'
import { collection, query, getDocs, where, orderBy, limit } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { Mail, Users, CheckCircle, XCircle, TrendingUp, Clock } from 'lucide-react'
import { formatDate, truncateText } from '../lib/utils'

interface Stats {
  totalLetters: number
  approvedLetters: number
  rejectedLetters: number
  totalUsers: number
  todayLetters: number
  todaySessions: number
}

interface Letter {
  id: string
  body: string
  status: 'pending' | 'approved' | 'rejected'
  createdAt: Date
  authorUid?: string
  rejectionReason?: string
  randomIndex?: number
}

interface RecentLetter {
  id: string
  body: string
  status: string
  createdAt: Date
}

export default function DashboardPage() {
  const [stats, setStats] = useState<Stats>({
    totalLetters: 0,
    approvedLetters: 0,
    rejectedLetters: 0,
    totalUsers: 0,
    todayLetters: 0,
    todaySessions: 0,
  })
  const [recentLetters, setRecentLetters] = useState<RecentLetter[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchStats()
  }, [])

  const fetchStats = async () => {
    try {
      // Get all letters
      const lettersSnapshot = await getDocs(collection(db, 'letters'))
      const letters: Letter[] = lettersSnapshot.docs.map(doc => {
        const data = doc.data()
        return {
          id: doc.id,
          body: data.body || '',
          status: data.status || 'pending',
          createdAt: data.createdAt?.toDate() || new Date(),
          authorUid: data.authorUid,
          rejectionReason: data.rejectionReason,
          randomIndex: data.randomIndex,
        }
      })

      // Get user profiles count
      const usersSnapshot = await getDocs(collection(db, 'userProfiles'))
      
      // Get today's data
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      const todayLetters = letters.filter(l => l.createdAt >= today)
      
      // Get today's sessions
      const sessionsQuery = query(
        collection(db, 'userSessions'),
        where('loginAt', '>=', today),
      )
      const sessionsSnapshot = await getDocs(sessionsQuery)

      // Get recent letters
      const recentQuery = query(
        collection(db, 'letters'),
        orderBy('createdAt', 'desc'),
        limit(5)
      )
      const recentSnapshot = await getDocs(recentQuery)
      const recent = recentSnapshot.docs.map(doc => ({
        id: doc.id,
        body: doc.data().body,
        status: doc.data().status,
        createdAt: doc.data().createdAt?.toDate() || new Date(),
      }))

      setStats({
        totalLetters: letters.length,
        approvedLetters: letters.filter(l => l.status === 'approved').length,
        rejectedLetters: letters.filter(l => l.status === 'rejected').length,
        totalUsers: usersSnapshot.size,
        todayLetters: todayLetters.length,
        todaySessions: sessionsSnapshot.size,
      })
      setRecentLetters(recent)
    } catch (error) {
      console.error('Error fetching stats:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Dashboard</h2>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Toplam Mektup"
          value={stats.totalLetters}
          icon={Mail}
          color="blue"
        />
        <StatCard
          title="Onaylanan"
          value={stats.approvedLetters}
          icon={CheckCircle}
          color="green"
        />
        <StatCard
          title="Reddedilen"
          value={stats.rejectedLetters}
          icon={XCircle}
          color="red"
        />
        <StatCard
          title="Toplam Kullanıcı"
          value={stats.totalUsers}
          icon={Users}
          color="purple"
        />
      </div>

      {/* Today Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Bugün Yazılan Mektup</p>
              <p className="text-2xl font-bold text-gray-900">{stats.todayLetters}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-cyan-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Bugün Oturum</p>
              <p className="text-2xl font-bold text-gray-900">{stats.todaySessions}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Letters */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">Son Mektuplar</h3>
        </div>
        <div className="divide-y divide-gray-100">
          {recentLetters.map((letter) => (
            <div key={letter.id} className="px-6 py-4 flex items-center gap-4">
              <div className={`w-2 h-2 rounded-full ${
                letter.status === 'approved' ? 'bg-green-500' : 'bg-red-500'
              }`} />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-900 truncate">
                  {truncateText(letter.body, 80)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {formatDate(letter.createdAt)}
                </p>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full ${
                letter.status === 'approved' 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-red-100 text-red-700'
              }`}>
                {letter.status === 'approved' ? 'Onaylı' : 'Reddedildi'}
              </span>
            </div>
          ))}
          {recentLetters.length === 0 && (
            <div className="px-6 py-8 text-center text-gray-500">
              Henüz mektup yok
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

interface StatCardProps {
  title: string
  value: number
  icon: React.ElementType
  color: 'blue' | 'green' | 'red' | 'purple'
}

function StatCard({ title, value, icon: Icon, color }: StatCardProps) {
  const colors = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    red: 'bg-red-100 text-red-600',
    purple: 'bg-purple-100 text-purple-600',
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colors[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
      </div>
    </div>
  )
}
